import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Outlet, useSearchParams } from 'react-router-dom';
import styles from './ExternalLayout.module.scss';
import ExternalHeader from './ExternalHeader/ExternalHeader';

const BREED_CACHE_KEY = 'external_portal_selectedBreed';

export interface ExternalBackLink {
    to: string;
    text: string;
    icon?: string;
    openInNewTab?: boolean;
}

export interface ExternalLayoutContext {
    breeds: any[];
    selectedBreed: string;
    handleBreedChange: (breedId: string) => void;
    setBackLink: (backLink: ExternalBackLink | undefined) => void;
    // Statistics covers every breed at once and picks them on the page, so it
    // turns the header's single-breed dropdown off while it is mounted.
    setShowBreedDropdown: (show: boolean) => void;
}

function ExternalLayout() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [breeds, setBreeds] = useState<any[]>([]);
    const [selectedBreed, setSelectedBreed] = useState('');
    const [backLink, setBackLink] = useState<ExternalBackLink | undefined>(undefined);
    const [showBreedDropdown, setShowBreedDropdown] = useState(true);

    useEffect(() => {
        axios.get('/api/breeds/all').then(res => {
            setBreeds(res.data || []);
        }).catch(() => setBreeds([]));
    }, []);

    useEffect(() => {
        if (breeds.length === 0) return;
        const rParam = searchParams.get('r');
        if (rParam && breeds.some((b: any) => b.ID.toString() === rParam)) {
            setSelectedBreed(rParam);
        } else if (selectedBreed === '') {
            try {
                const cached = localStorage.getItem(BREED_CACHE_KEY) || '';
                if (cached && breeds.some((b: any) => b.ID.toString() === cached)) {
                    setSelectedBreed(cached);
                    setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('r', cached); return next; }, { replace: true });
                }
            } catch {}
        }
    }, [breeds]);

    function handleBreedChange(breedId: string) {
        setSelectedBreed(breedId);
        if (breedId) {
            try { localStorage.setItem(BREED_CACHE_KEY, breedId); } catch {}
            setSearchParams(prev => { const next = new URLSearchParams(prev); next.set('r', breedId); return next; });
        } else {
            try { localStorage.removeItem(BREED_CACHE_KEY); } catch {}
            setSearchParams(prev => { const next = new URLSearchParams(prev); next.delete('r'); return next; });
        }
    }

    return (
        <div className={styles.externalLayout}>
            <div className={styles.betaRibbon}>
                <span>BETA</span>
            </div>
            <ExternalHeader
                showBreedDropdown={showBreedDropdown}
                breeds={breeds}
                selectedBreed={selectedBreed}
                onBreedChange={handleBreedChange}
                backLink={backLink}
            />
            <Outlet context={{ breeds, selectedBreed, handleBreedChange, setBackLink, setShowBreedDropdown } as ExternalLayoutContext} />
        </div>
    );
}

export default ExternalLayout;
